import { useCallback, useMemo } from 'react';
import type { ReactNode } from 'react';
import type { Customer } from '@/services/db/customer/entity';
import { isUnknownPeopleTotal } from '@/services/db/customer/entity';
import { formatNumber } from '@/utils/number';
import plur from 'plur';
import { isEmptyString } from '@/utils/string';
import { createPeopleColumns } from './people-table-columns';
import { HiddenResultsEmptyState, NoFiltersEmptyState } from './people-table-empty-states';
import type { PeopleTableProps } from './people-table';

const EMPTY_PENDING_DELETION: ReadonlySet<string> = new Set();

/** The shape `DataTable` renders its `emptyStateComponent` with. */
// The empty states never read `table`, so this deliberately does not pin a row
// type: v9's `Table` is invariant in TData, and DataTable hands the prop over as
// `Table<…, any>`, which a concrete `Customer` would reject.
type PeopleEmptyStateComponent = (props: { table?: unknown }) => ReactNode;

/**
 * Builds the memoized `DataTable` inputs for `PeopleTable`: row data, stable
 * column defs, the selectability predicate, the pagination config, the
 * empty-state pair (component vs config, with the loading → hidden →
 * no-filters → no-match precedence), and the derived selection copy.
 */
export function usePeopleTable(props: PeopleTableProps) {
  const {
    people,
    isLoading,
    pendingDeletionIds = EMPTY_PENDING_DELETION,
    allAttributes,
    pagination,
    allSelected,
    totalCount,
    selectedCount,
    hasFilters,
    hasHiddenResults,
    email = '',
    shouldRedactCreatedAt = false,
    addPeopleAction,
  } = props;

  const data = useMemo(() => [...(people ?? [])], [people]);

  const columns = useMemo(
    () => createPeopleColumns({ allAttributes, shouldRedactCreatedAt }),
    [allAttributes, shouldRedactCreatedAt],
  );

  const getRowCanSelect = useCallback(
    (row: { original: Customer }) => !pendingDeletionIds.has(row.original.id),
    [pendingDeletionIds],
  );

  const paginationConfig = useMemo(() => ({ ...pagination, isManual: true }), [pagination]);

  const emptyStateComponent = useMemo((): PeopleEmptyStateComponent | undefined => {
    if (isLoading === true) return undefined;
    if (hasHiddenResults) return HiddenResultsEmptyState;
    if (!hasFilters) {
      function PeopleNoFiltersEmptyState() {
        return <NoFiltersEmptyState action={addPeopleAction} />;
      }
      return PeopleNoFiltersEmptyState;
    }
    return undefined;
  }, [isLoading, hasHiddenResults, hasFilters, addPeopleAction]);

  const emptyState = useMemo(() => {
    if (hasFilters && !hasHiddenResults && isLoading !== true) {
      return {
        title: isEmptyString(email) ? 'No people matching your filters' : 'No people matching your search',
        icon: 'person' as const,
      };
    }
    return undefined;
  }, [hasFilters, hasHiddenResults, isLoading, email]);

  const unknownTotal = isUnknownPeopleTotal(totalCount);
  const hasAnySelection = allSelected || selectedCount > 0;
  const peopleToSelectCount = `${formatNumber(totalCount)} ${plur('person', totalCount)}`;

  return {
    data,
    columns,
    getRowCanSelect,
    paginationConfig,
    emptyState,
    emptyStateComponent,
    unknownTotal,
    hasAnySelection,
    peopleToSelectCount,
    peopleSingular: 'person',
    peoplePlural: 'people',
    peopleTitlePlural: 'People',
  };
}
